import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useOrders } from '../hooks/useOrders';
import { OrderCard } from './OrderCard';
import { OrderFilters } from './OrderFilters';
import { OrderDetailView } from './OrderDetailView';
import type { Order } from '../types/Order';
interface OrdersOverviewProps {
  onBack: () => void;
  onStartInspection?: (order: Order) => void;
}
export function OrdersOverview({
  onBack,
  onStartInspection
}: OrdersOverviewProps) {
  const {
    activeOrders,
    pausedOrders,
    completedOrders,
    showCompleted,
    toggleCompleted,
    counts,
    getInspections
  } = useOrders();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const hasOrders = counts.active > 0 || counts.paused > 0 || counts.completed > 0;
  const handleAddSample = () => {
    if (selectedOrder && onStartInspection) {
      onStartInspection(selectedOrder);
    }
  };
  const handleContinue = () => {
    // TODO: Implement continue logic
    console.log('Continue order:', selectedOrder?.id);
  };
  const handlePause = () => {
    // Close detail view and return to orders overview
    console.log('Pause order:', selectedOrder?.id);
    setSelectedOrder(null);
  };
  const handleComplete = () => {
    // Close detail view and return to orders overview
    console.log('Complete order:', selectedOrder?.id);
    setSelectedOrder(null);
  };
  // Show detail view if an order is selected
  if (selectedOrder) {
    return <OrderDetailView order={selectedOrder} inspections={getInspections(selectedOrder.id)} onBack={() => setSelectedOrder(null)} onAddSample={selectedOrder.status === 'active' ? handleAddSample : undefined} onContinue={selectedOrder.status === 'paused' ? handleContinue : undefined} onPause={selectedOrder.status === 'active' ? handlePause : undefined} onComplete={selectedOrder.status !== 'completed' ? handleComplete : undefined} data-id="element-595" />;
  }
  return <div className="min-h-screen w-full bg-white" data-id="element-596">
      <div className="max-w-7xl mx-auto px-8 py-12" data-id="element-597">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-598">
          <div className="flex items-center gap-4 mb-8" data-id="element-599">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Zurück" data-id="element-600">
              <ArrowLeft className="w-6 h-6 text-gray-600" strokeWidth={2} data-id="element-601" />
            </button>
            <h1 className="text-4xl font-semibold text-gray-900" data-id="element-602">
              Auftragsübersicht
            </h1>
          </div>

          {!hasOrders ? <div className="flex items-center justify-center min-h-[60vh]" data-id="element-603">
              <div className="text-center space-y-4" data-id="element-604">
                <div className="text-6xl mb-4" data-id="element-605">📋</div>
                <p className="text-xl text-gray-500" data-id="element-606">
                  Noch keine Aufträge vorhanden
                </p>
              </div>
            </div> : <div className="space-y-8" data-id="element-607">
              <OrderFilters showCompleted={showCompleted} onToggleCompleted={toggleCompleted} activeCount={counts.active} pausedCount={counts.paused} completedCount={counts.completed} data-id="element-608" />

              {/* Active Orders */}
              {activeOrders.length > 0 && <div className="space-y-4" data-id="element-609">
                  <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" data-id="element-610">
                    <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-611" />
                    Aktive Aufträge
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-612">
                    <AnimatePresence mode="popLayout" data-id="element-613">
                      {activeOrders.map(order => <OrderCard key={order.id} order={order} onClick={() => setSelectedOrder(order)} data-id="element-614" />)}
                    </AnimatePresence>
                  </div>
                </div>}

              {/* Paused Orders */}
              {pausedOrders.length > 0 && <div className="space-y-4" data-id="element-615">
                  <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" data-id="element-616">
                    <div className="w-3 h-3 rounded-full bg-yellow-600" data-id="element-617" />
                    Pausierte Aufträge
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-618">
                    <AnimatePresence mode="popLayout" data-id="element-619">
                      {pausedOrders.map(order => <OrderCard key={order.id} order={order} onClick={() => setSelectedOrder(order)} data-id="element-620" />)}
                    </AnimatePresence>
                  </div>
                </div>}

              {/* Completed Orders */}
              {completedOrders.length > 0 && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="space-y-4" data-id="element-621">
                  <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" data-id="element-622">
                    <div className="w-3 h-3 rounded-full bg-green-600" data-id="element-623" />
                    Abgeschlossene Aufträge
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-624">
                    <AnimatePresence mode="popLayout" data-id="element-625">
                      {completedOrders.map(order => <OrderCard key={order.id} order={order} onClick={() => setSelectedOrder(order)} data-id="element-626" />)}
                    </AnimatePresence>
                  </div>
                </motion.div>}
            </div>}
        </motion.div>
      </div>
    </div>;
}